/**
 * Single source of truth for amount-input caps.
 *
 * Today: takes a token's `decimals` and returns `{ maxDecimals,
 * maxIntegerDigits }` — the values plumbed into `AmountInput` (and through
 * to `sanitizeDecimal`). Tomorrow: extend to consider balance, network
 * limits, slippage caps, etc., without each consumer having to derive caps
 * independently.
 *
 * Usage:
 * ```ts
 * const caps = useInputCaps({ tokenDecimals: swap.sendTokenDecimals });
 * <AmountInput {...caps} ... />
 * ```
 *
 * Or with explicit overrides:
 * ```ts
 * const caps = useInputCaps({ tokenDecimals: 6, maxIntegerDigits: 12 });
 * ```
 */

interface UseInputCapsOptions {
    /** Token decimal precision (e.g. USDC=6, USDv=9). When undefined,
     *  falls back to `9` so the cap doesn't accidentally narrow below
     *  USDv. Pass it from the active token's metadata. */
    tokenDecimals?: number;
    /** Override the default integer-digit ceiling. Defaults to `15` —
     *  past where number formatters switch to scientific notation, but
     *  still a sane defensive floor for any real-world balance. */
    maxIntegerDigits?: number;
}

interface InputCaps {
    /** Max digits allowed AFTER the decimal point. */
    maxDecimals: number;
    /** Max digits allowed BEFORE the decimal point. */
    maxIntegerDigits: number;
}

/** Default decimal cap when no token decimals are provided.
 *  Set to USDv's precision so the fallback never narrows the input. */
const DEFAULT_MAX_DECIMALS = 9;

/** Default integer ceiling. 15 digits = up to 1 quadrillion, well past
 *  where JS `toFixed`/`toString` would emit scientific notation. */
const DEFAULT_MAX_INTEGER_DIGITS = 15;

export function useInputCaps({ tokenDecimals, maxIntegerDigits }: UseInputCapsOptions = {}): InputCaps {
    return {
        maxDecimals: tokenDecimals ?? DEFAULT_MAX_DECIMALS,
        maxIntegerDigits: maxIntegerDigits ?? DEFAULT_MAX_INTEGER_DIGITS
    };
}
